import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Btn } from '../components/shared/UI';
import LanguageSwitcher from '../components/shared/LanguageSwitcher';
import PasswordStrength, { checkPassword } from '../components/shared/PasswordStrength';
import { useT } from '../i18n';
import api from '../utils/api';

const field = {
  width: '100%', padding: '11px 14px', border: '1.5px solid var(--border)',
  borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none',
  background: 'var(--input-bg)', color: 'var(--text)', boxSizing: 'border-box',
};
const label = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 };

// Landing page for the link in the "Reset your password" email (/reset-password?token=…)
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useT();
  const token = params.get('token') || '';
  // 'checking' → 'form' → 'success', or 'invalid' when the link is dead
  const [state, setState] = useState({ status: 'checking', message: '' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [show, setShow]         = useState(false);
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ status: 'invalid', message: '' });
      return;
    }
    api.get(`/auth/reset-password/check?token=${encodeURIComponent(token)}`)
      .then(() => setState({ status: 'form', message: '' }))
      .catch(e => setState({ status: 'invalid', message: e.response?.data?.message || '' }));
  }, [token]);

  const submit = async e => {
    e.preventDefault();
    setError('');
    if (!checkPassword(password).valid) return setError(t('auth.weakPassword'));
    if (password !== confirm) return setError(t('reset.mismatch'));
    setSaving(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      // Any session on this device predates the reset and is no longer valid
      localStorage.removeItem('token');
      localStorage.removeItem('adminToken');
      setState({ status: 'success', message: '' });
    } catch (err) {
      const code = err.response?.data?.code;
      const message = err.response?.data?.message || t('reset.failedGeneric');
      if (code === 'resetInvalid' || code === 'resetExpired') setState({ status: 'invalid', message });
      else setError(message);
    } finally { setSaving(false); }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, background: 'linear-gradient(135deg, #0f2447 0%, #1A3C6E 50%, #2E75B6 100%)',
      fontFamily: 'inherit', position: 'relative',
    }}>
      <div style={{ position: 'absolute', top: 20, insetInlineEnd: 20 }}>
        <LanguageSwitcher variant="dark" compact />
      </div>

      <div style={{
        width: '100%', maxWidth: 460, background: 'var(--card-bg)', borderRadius: 16,
        padding: '40px 36px', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', textAlign: 'center',
      }}>
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontSize: 36, fontWeight: 900, color: '#F59E0B', letterSpacing: '-2px' }}>I</span>
          <span style={{ fontSize: 36, fontWeight: 700, color: 'var(--heading)', letterSpacing: '-1.5px' }}>nvio</span>
        </div>

        {state.status === 'checking' && (
          <>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{t('reset.checking')}</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('verify.justAMoment')}</p>
          </>
        )}

        {state.status === 'form' && (
          <form onSubmit={submit} style={{ textAlign: 'start', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--heading)', marginBottom: 8 }}>{t('reset.title')}</h2>
              <p style={{ fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>{t('reset.intro')}</p>
            </div>

            <div>
              <label htmlFor="new-password" style={label}>{t('reset.newPassword')}</label>
              <input id="new-password" type={show ? 'text' : 'password'} required autoFocus autoComplete="new-password"
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder={t('auth.passwordPlaceholder')} style={field} />
              <PasswordStrength password={password} />
            </div>

            <div>
              <label htmlFor="confirm-password" style={label}>{t('reset.confirmPassword')}</label>
              <input id="confirm-password" type={show ? 'text' : 'password'} required autoComplete="new-password"
                value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder={t('auth.passwordPlaceholder')} style={field} />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)}
                style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--navy)' }} />
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('auth.showPassword')}</span>
            </label>

            {error && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1.5px solid rgba(239,68,68,0.4)', borderRadius: 8, padding: '11px 14px', fontSize: 13, color: 'var(--danger)' }}>
                {error}
              </div>
            )}

            <Btn variant="primary" type="submit" disabled={saving} style={{ width: '100%', justifyContent: 'center' }}>
              {saving ? t('auth.pleaseWait') : t('reset.submit')}
            </Btn>
          </form>
        )}

        {state.status === 'success' && (
          <>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--heading)', marginBottom: 8 }}>{t('reset.success')}</h2>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 24 }}>{t('reset.successBody')}</p>
            {/* Full page load, not navigate(): the auth context may still hold the signed-out session */}
            <Btn variant="primary" onClick={() => { window.location.href = '/login'; }} style={{ width: '100%', justifyContent: 'center' }}>
              {t('auth.signInBtn')}
            </Btn>
          </>
        )}

        {state.status === 'invalid' && (
          <>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--danger)', marginBottom: 8 }}>{t('reset.invalidTitle')}</h2>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 24 }}>{state.message || t('reset.invalidGeneric')}</p>
            <Btn variant="primary" onClick={() => navigate('/login?view=forgot')} style={{ width: '100%', justifyContent: 'center', marginBottom: 10 }}>
              {t('reset.requestNew')}
            </Btn>
            <Btn variant="ghost" onClick={() => navigate('/login')} style={{ width: '100%', justifyContent: 'center' }}>
              {t('auth.backToSignInBtn')}
            </Btn>
          </>
        )}
      </div>
    </div>
  );
}
